import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FiTarget, FiEye, FiHeart, FiAward, FiUsers, FiBook } from "react-icons/fi";
import { useSiteSettings } from "../context/SiteSettingsContext";

const fallbackLeadership = [
  { name: "Dr. Ramesh Kumar Sharma", role: "Principal", description: "PhD in Education, 25+ years of experience in educational leadership." },
  { name: "Mrs. Sunita Thapa", role: "Vice Principal", description: "Masters in Education Administration, dedicated to student welfare." },
  { name: "Mr. Bikram Prasad", role: "Academic Director", description: "PhD in Curriculum Development, expert in modern pedagogy." },
];

const fallbackAchievements = [
  { title: "Best School Award 2024", desc: "Recognized by Nepal Education Board for academic excellence" },
  { title: "5000+ Alumni Network", desc: "Graduates serving in top positions globally" },
  { title: "98% Pass Rate", desc: "Consistently high pass rate in board examinations" },
  { title: "ISO Certified", desc: "ISO 9001:2015 certified for quality management" },
];

const achievementIcons = [FiAward, FiUsers, FiBook, FiAward];

const getInitials = (name) =>
  name ? name.split(" ").filter(Boolean).map((n) => n[0]).join("").toUpperCase().slice(0, 2) : "??";

const SectionLabel = ({ text }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className="w-8 h-1 rounded-full bg-[#2563EB]" />
    <span className="text-[#2563EB] text-xs font-bold uppercase tracking-[0.25em]">{text}</span>
  </div>
);

const About = () => {
  const [leadership, setLeadership] = useState(fallbackLeadership);
  const [achievements, setAchievements] = useState(fallbackAchievements);
  const { settings } = useSiteSettings();

  useEffect(() => {
    if (settings) {
      if (settings.leadership?.length > 0) setLeadership(settings.leadership);
      if (settings.achievements?.length > 0) setAchievements(settings.achievements);
    }
  }, [settings]);

  return (
    <div className="bg-white">
      {/* Hero */}
      <div className="bg-[#1e3a8a] py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: "radial-gradient(circle, #ffffff 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#2563EB]" />
        <div className="relative max-w-7xl mx-auto px-8 sm:px-12 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-1 rounded-full bg-sky-400" />
              <p className="text-sky-400 text-xs font-bold uppercase tracking-[0.25em]">Know Us Better</p>
            </div>
            <h1 className="text-4xl sm:text-6xl font-bold text-white leading-tight mb-6">
              About <span className="text-sky-300">Us</span>
            </h1>
            <p className="text-blue-200/70 text-lg max-w-xl leading-relaxed">
              Discover our story, values, and the people who make{" "}
              {settings?.schoolName || "Early Boarding School & Montessori"} exceptional.
            </p>
          </motion.div>
        </div>
      </div>

      {/* School History */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-8 sm:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
              <SectionLabel text="Our History" />
              <h2 className="text-4xl font-bold text-[#0F172A] mb-6">
                A Journey of <span className="text-[#2563EB]">Excellence</span>
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed text-sm">
                <p>
                  Early Boarding School & Montessori was founded in 1999 with a vision to provide quality education
                  that nurtures both academic excellence and personal growth. Starting with just 200 students and 15
                  dedicated teachers, we have grown into one of Kathmandu's most respected educational institutions.
                </p>
                <p>
                  Over the past 25 years, we have continuously evolved our curriculum, infrastructure, and teaching
                  methodologies to keep pace with global educational standards while preserving our core values of
                  integrity, respect, and excellence.
                </p>
              </div>
            </motion.div>

            {/* Timeline */}
            <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }} className="space-y-5">
              {[
                { year: "1999", event: "Founded with 200 students",                color: "bg-[#2563EB]" },
                { year: "2005", event: "Expanded to Secondary School level",       color: "bg-[#1e3a8a]" },
                { year: "2010", event: "Added High School program",                color: "bg-[#2563EB]" },
                { year: "2015", event: "Inaugurated new science and computer labs", color: "bg-[#1e3a8a]" },
                { year: "2020", event: "Launched smart classroom initiative",       color: "bg-[#2563EB]" },
                { year: "2024", event: "Awarded Best School by NEB",               color: "bg-[#1e3a8a]" },
              ].map((item, idx) => (
                <div key={idx} className="flex gap-4 items-start group">
                  <div className={`w-12 h-12 ${item.color} rounded-xl flex items-center justify-center text-white font-bold text-sm shrink-0 transition-transform group-hover:scale-110 shadow-sm`}>
                    {item.year.slice(2)}
                  </div>
                  <div className="pt-2 border-l-2 border-blue-100 pl-4 flex-1">
                    <p className="font-bold text-[#0F172A] text-sm">{item.year}</p>
                    <p className="text-slate-500 text-sm">{item.event}</p>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="py-20 lg:py-28 bg-[#F0F4FF]">
        <div className="max-w-7xl mx-auto px-8 sm:px-12 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="mb-14">
            <SectionLabel text="Our Foundation" />
            <h2 className="text-4xl font-bold text-[#0F172A]">Mission &amp; <span className="text-[#2563EB]">Values</span></h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: FiTarget, title: "Our Mission", desc: "To provide a nurturing learning environment that empowers every student to achieve academic excellence, develop strong moral values, and become responsible global citizens." },
              { icon: FiEye,    title: "Our Vision",  desc: "To be the leading educational institution in Nepal, recognized for producing well-rounded graduates who contribute meaningfully to society and the world at large." },
              { icon: FiHeart, title: "Our Values",  desc: "Excellence, Integrity, Respect, Innovation, Compassion, and Community — these core values guide every aspect of life at Early Boarding School & Montessori." },
            ].map((card, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-8 border border-blue-100 shadow-sm hover:shadow-lg hover:border-[#2563EB]/30 transition-all duration-300 group"
              >
                <div className="w-12 h-12 bg-[#2563EB] rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#1D4ED8] transition-colors shadow-sm">
                  <card.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#0F172A] mb-4">{card.title}</h3>
                <p className="text-slate-500 leading-relaxed text-sm">{card.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-8 sm:px-12 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="mb-14">
            <SectionLabel text="Our Leadership" />
            <h2 className="text-4xl font-bold text-[#0F172A]">Meet the <span className="text-[#2563EB]">Administration</span></h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {leadership.map((person, idx) => (
              <motion.div
                key={person._id || idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-8 border border-blue-100 shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col items-center text-center"
              >
                <div className="w-24 h-24 rounded-2xl overflow-hidden mb-5 shrink-0 border-2 border-blue-100">
                  {person.photo?.url ? (
                    <img src={person.photo.url} alt={person.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-[#2563EB] flex items-center justify-center text-white text-xl font-bold">
                      {getInitials(person.name)}
                    </div>
                  )}
                </div>
                <h3 className="font-bold text-[#0F172A] text-lg mb-1">{person.name}</h3>
                <p className="text-[#2563EB] text-xs font-bold uppercase tracking-widest mb-3">{person.role}</p>
                <p className="text-slate-500 text-sm leading-relaxed">{person.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="py-20 lg:py-28 bg-[#1e3a8a]">
        <div className="max-w-7xl mx-auto px-8 sm:px-12 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="mb-14">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-1 rounded-full bg-sky-400" />
              <span className="text-sky-400 text-xs font-bold uppercase tracking-[0.25em]">Our Pride</span>
            </div>
            <h2 className="text-4xl font-bold text-white">Achievements &amp; <span className="text-sky-300">Recognition</span></h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {achievements.map((item, idx) => {
              const Icon = achievementIcons[idx % achievementIcons.length];
              return (
                <motion.div
                  key={item._id || idx}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white/10 rounded-2xl p-8 text-center border border-white/20 hover:bg-white/20 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-5 group-hover:bg-[#2563EB] transition-colors">
                    <Icon className="w-6 h-6 text-sky-300 group-hover:text-white transition-colors" />
                  </div>
                  <h4 className="font-bold text-white text-sm mb-2">{item.title}</h4>
                  <p className="text-blue-200/70 text-xs leading-relaxed">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
